if (!customElements.get('mobile-sticky-add-to-cart')) {
  customElements.define(
    'mobile-sticky-add-to-cart',
    class MobileStickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true' || this.closest('quick-add-modal')) return;

        this.productSection = this.closest('[id^="MainProduct-"]');
        this.sourceButton = this.productSection?.querySelector(
          'form[action*="/cart/add"] .product-form__submit[name="add"]'
        );
        this.proxyButton = this.querySelector('[data-sticky-add-to-cart-button]');
        this.proxyLabel = this.querySelector('[data-sticky-add-to-cart-label]');
        this.proxySpinner = this.querySelector('[data-sticky-add-to-cart-spinner]');
        this.sesamiContainer = this.querySelector('[data-sticky-sesami-container]');
        this.sesamiProxyButton = this.querySelector('[data-sticky-sesami-button]');
        this.isSesamiService = this.productSection?.hasAttribute('data-sesami-service-ateliers');

        if (
          !this.productSection ||
          (!this.isSesamiService && (!this.sourceButton || !this.proxyButton))
        ) {
          return;
        }

        this.dataset.initialized = 'true';
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.handleProxyClick = this.handleProxyClick.bind(this);
        this.handleSesamiProxyClick = this.handleSesamiProxyClick.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.syncButtonState = this.syncButtonState.bind(this);
        this.syncSesamiButtonState = this.syncSesamiButtonState.bind(this);
        this.handleSesamiBookingLoading = this.handleSesamiBookingLoading.bind(this);
        this.updateStickyHeight = this.updateStickyHeight.bind(this);

        this.proxyButton?.addEventListener('click', this.handleProxyClick);
        this.sesamiProxyButton?.addEventListener('click', this.handleSesamiProxyClick);
        window.addEventListener('theme:sesami-booking-loading', this.handleSesamiBookingLoading);

        if (this.sourceButton) {
          this.buttonObserver = new MutationObserver(this.syncButtonState);
          this.buttonObserver.observe(this.sourceButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled'],
            characterData: true,
            childList: true,
            subtree: true
          });
        }

        this.initializeSesamiExperience();
        this.sesamiObserver = new MutationObserver(() => this.initializeSesamiExperience());
        this.sesamiObserver.observe(this.productSection, {
          childList: true,
          subtree: true
        });

        this.resizeObserver = new ResizeObserver(this.updateStickyHeight);
        this.resizeObserver.observe(this);

        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this.updateVisibility);
        } else {
          this.mobileMediaQuery.addListener(this.updateVisibility);
        }

        this.syncButtonState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.proxyButton?.removeEventListener('click', this.handleProxyClick);
        this.sesamiProxyButton?.removeEventListener('click', this.handleSesamiProxyClick);
        window.removeEventListener('theme:sesami-booking-loading', this.handleSesamiBookingLoading);
        this.buttonObserver?.disconnect();
        this.sesamiObserver?.disconnect();
        this.sesamiButtonObserver?.disconnect();
        this.resizeObserver?.disconnect();
        window.clearInterval(this.sesamiButtonInterval);
        this.sesamiExperience?.removeAttribute('data-sticky-sesami-source');
        document.documentElement.classList.remove('mobile-sticky-add-to-cart-enabled');
        document.documentElement.style.removeProperty('--mobile-sticky-add-to-cart-height');

        if (!this.mobileMediaQuery) return;

        if (this.mobileMediaQuery.removeEventListener) {
          this.mobileMediaQuery.removeEventListener('change', this.updateVisibility);
        } else {
          this.mobileMediaQuery.removeListener(this.updateVisibility);
        }
      }

      handleProxyClick() {
        if (!this.sourceButton || this.proxyButton.disabled || this.sourceButton.disabled) return;

        this.proxyButton.disabled = true;
        this.sourceButton.click();
        queueMicrotask(this.syncButtonState);
      }

      handleSesamiProxyClick() {
        const sesamiButton = this.sesamiExperience?.shadowRoot?.querySelector('.sesami-calendar');
        if (!sesamiButton || sesamiButton.disabled) return;

        sesamiButton.click();
      }

      syncButtonState() {
        if (!this.sourceButton) return;

        const sourceLabel = this.sourceButton.querySelector('span')?.textContent.trim();
        const isLoading = this.sourceButton.classList.contains('loading');
        const isDisabled =
          this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';

        if (sourceLabel && this.proxyLabel) this.proxyLabel.textContent = sourceLabel;

        this.proxyButton.disabled = isDisabled;
        this.proxyButton.classList.toggle('loading', isLoading);
        this.proxyLabel?.classList.toggle('hidden', isLoading);
        this.proxySpinner?.classList.toggle('hidden', !isLoading);

        if (this.sourceButton.hasAttribute('aria-haspopup')) {
          this.proxyButton.setAttribute('aria-haspopup', this.sourceButton.getAttribute('aria-haspopup'));
        } else {
          this.proxyButton.removeAttribute('aria-haspopup');
        }
      }

      initializeSesamiExperience() {
        if (this.sesamiExperience || !this.sesamiContainer || !this.sesamiProxyButton) return;

        const experience = this.productSection.querySelector('sesami-experience');
        if (!experience) return;

        this.sesamiExperience = experience;
        this.sesamiExperience.setAttribute('data-sticky-sesami-source', '');
        if (this.proxyButton) this.proxyButton.hidden = this.isSesamiService;
        this.sesamiContainer.hidden = false;
        this.syncSesamiButtonState();

        this.sesamiButtonAttempts = 0;
        this.sesamiButtonInterval = window.setInterval(() => {
          this.sesamiButtonAttempts += 1;
          if (this.syncSesamiButtonState() || this.sesamiButtonAttempts >= 100) {
            window.clearInterval(this.sesamiButtonInterval);
          }
        }, 100);
      }

      handleSesamiBookingLoading(event) {
        if (
          event.detail?.tagId &&
          String(event.detail.tagId) !== String(this.sesamiExperience?.getAttribute('tag-id'))
        ) {
          return;
        }

        if (event.detail?.loading) {
          this.sesamiProxyButton.disabled = true;
        } else {
          this.syncSesamiButtonState();
        }
      }

      syncSesamiButtonState() {
        const sesamiButton = this.sesamiExperience?.shadowRoot?.querySelector('.sesami-calendar');
        const isReady = Boolean(sesamiButton);
        const isDisabled =
          !isReady || sesamiButton.disabled || sesamiButton.getAttribute('aria-disabled') === 'true';

        if (isReady && !this.sesamiButtonObserver) {
          this.sesamiButtonObserver = new MutationObserver(this.syncSesamiButtonState);
          this.sesamiButtonObserver.observe(sesamiButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled']
          });
        }

        this.sesamiProxyButton.disabled = isDisabled;
        this.classList.toggle('mobile-sticky-add-to-cart--with-booking', isReady);
        requestAnimationFrame(this.updateStickyHeight);
        return isReady && !isDisabled;
      }

      updateStickyHeight() {
        if (!this.mobileMediaQuery?.matches || this.hidden) {
          document.documentElement.style.removeProperty('--mobile-sticky-add-to-cart-height');
          return;
        }

        document.documentElement.style.setProperty(
          '--mobile-sticky-add-to-cart-height',
          `${this.getBoundingClientRect().height}px`
        );
      }

      updateVisibility() {
        const shouldShow = this.mobileMediaQuery.matches;

        this.hidden = !shouldShow;
        document.documentElement.classList.toggle('mobile-sticky-add-to-cart-enabled', shouldShow);
        requestAnimationFrame(this.updateStickyHeight);
      }
    }
  );
}
